import { Autocomplete, type AutocompleteRenderInputParams, Checkbox, Collapse, FormControlLabel, Grid, TextField } from '@mui/material'
import { useAppSelector } from '../../../Common/_hooks/useAppSelector'
import { type ChangeEvent, type FocusEvent, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { UserAddressStore } from '../../../UserData/_stores/UserAddressStore'
import { useTranslation } from 'react-i18next'
import type { E164Number } from 'libphonenumber-js/core'
import PhoneField from '../../../Common/Form/PhoneField'
import { getSupportedPhoneCountryCode, phoneNumberIsPossible } from '../../../Common/Form/PhoneUtils'

interface Props {
  address: UserAddressStore
  onChange?: (value: (address: UserAddressStore) => UserAddressStore) => void
  onValidCheck: (valid: boolean) => void
  save?: boolean
  onChangeSave?: (value: boolean) => void
  disabled?: boolean
}

const normalizeAddressAutofillInput = (value: string) => value.trim().toLowerCase()

export default function AddressForm(props: Props) {
  const [t] = useTranslation('account-v2')
  const countries = useAppSelector(state => state.get('appData').get('countries'))
  const [countryOpen, setCountryOpen] = useState(false)
  const [provinceInput, setProvinceInput] = useState(props.address.get('province'))
  const preventNextCountryOpenRef = useRef(false)

  const onChangeName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(address => address.set('name', e.target.value))
  }, [props.onChange])

  const onChangeAddress = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(address => address.set('address', e.target.value))
  }, [props.onChange])

  const onChangeAddress2 = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(address => address.set('address2', e.target.value))
  }, [props.onChange])

  const onChangePostalCode = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(address => address.set('postal_code', e.target.value.toUpperCase()))
  }, [props.onChange])

  const onChangeCity = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(address => address.set('city', e.target.value))
  }, [props.onChange])

  const onChangePhone = useCallback((value?: E164Number) => {
    props.onChange?.(address => address.set('phone', value ?? ''))
  }, [props.onChange])

  const onChangeCountry = useCallback((e: SyntheticEvent, value: { id: number, label: string } | null) => {
    setCountryOpen(false)
    props.onChange?.(address => address.set('country', value ? value.id : 0))
  }, [props.onChange])

  const updateCountryFromInput = useCallback((value: string, matchCountryCodes = false) => {
    const input = normalizeAddressAutofillInput(value)
    if (!input) return false

    const countryObj = countries.find(country => {
      if (normalizeAddressAutofillInput(country.get('name')) === input) return true
      if (!matchCountryCodes) return false

      return country.get('alpha2_code').toLowerCase() === input || country.get('alpha3_code').toLowerCase() === input
    })

    if (!countryObj) return false
    if (countryObj.get('id') === props.address.get('country')) return true

    props.onChange?.(address => address.set('country', countryObj.get('id')))
    return true
  }, [countries, props.address.get('country'), props.onChange])

  const closeCountryAfterInputMatch = useCallback(() => {
    preventNextCountryOpenRef.current = true
    setCountryOpen(false)

    window.setTimeout(() => {
      preventNextCountryOpenRef.current = false
    })
  }, [])

  const onChangeCountryInput = useCallback((e: SyntheticEvent, value: string) => {
    if (updateCountryFromInput(value)) {
      closeCountryAfterInputMatch()
    }
  }, [closeCountryAfterInputMatch, updateCountryFromInput])

  const onBlurCountryInput = useCallback((e: FocusEvent<HTMLInputElement>) => {
    if (updateCountryFromInput(e.target.value, true)) {
      closeCountryAfterInputMatch()
    }
  }, [closeCountryAfterInputMatch, updateCountryFromInput])

  const onOpenCountry = useCallback(() => {
    if (preventNextCountryOpenRef.current) {
      preventNextCountryOpenRef.current = false
      setCountryOpen(false)
      return
    }

    setCountryOpen(true)
  }, [])

  const onCloseCountry = useCallback(() => {
    setCountryOpen(false)
  }, [])

  const onChangeDefaultShipping = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(address => address.set('default_shipping', e.target.checked))
  }, [props.onChange])

  const onChangeDefaultBilling = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChange?.(address => address.set('default_billing', e.target.checked))
  }, [props.onChange])

  const onChangeSave = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    props.onChangeSave?.(e.target.checked)
  }, [props.onChangeSave])

  // Autoselect country based on geo info
  useEffect(() => {
    if (!props.address.get('country') && window.APP_GEO_COUNTRYCODE) {
      const countryObj = countries.find(country => country.get('alpha2_code') === window.APP_GEO_COUNTRYCODE)
      props.onChange?.(address => address.set('country', countryObj ? countryObj.get('id') : 0))
    }
  }, [countries, props.onChange])

  const countryOptions = useMemo(() => {
    return countries.sortBy(country => country.get('name')).valueSeq().map(country => {
      return {
        id: country.get('id'),
        label: country.get('name')
      }
    }).toArray()
  }, [countries])

  const countryOptionValue = useMemo(() => {
    const countryObj = countries.get(String(props.address.get('country')))
    if (!countryObj) return null

    return {
      id: countryObj.get('id'),
      label: countryObj.get('name')
    }
  }, [props.address.get('country'), countries])

  const selectedCountry = useMemo(() => {
    return countries.get(String(props.address.get('country')))
  }, [countries, props.address.get('country')])

  const isUSAddress = useMemo(() => {
    return selectedCountry?.get('alpha2_code') === 'US'
  }, [selectedCountry])

  const listProvinces = useMemo(() => {
    return countries.get(String(props.address.get('country')))?.get('provinces')
  }, [countries, props.address.get('country')])

  const findProvinceFromInput = useCallback((value: string, matchProvinceCodes = false) => {
    const input = normalizeAddressAutofillInput(value)
    if (!input || !listProvinces || listProvinces.count() === 0) return undefined

    return listProvinces.find(province => {
      if (normalizeAddressAutofillInput(province.get('name')) === input) return true
      if (!matchProvinceCodes) return false

      return province.get('alpha2_code').toLowerCase() === input
    })
  }, [listProvinces])

  const provinceOptions = useMemo(() => {
    if (!listProvinces || listProvinces.count() === 0) return []

    return listProvinces.valueSeq().map(province => {
      return {
        name: province.get('name'),
        label: province.get('name'),
      }
    }).toArray()
  }, [listProvinces])

  const provinceOptionValue = useMemo(() => {
    return provinceOptions.find(option => option.name === props.address.get('province')) ?? null
  }, [provinceOptions, props.address.get('province')])

  const hasProvinceOptions = provinceOptions.length > 0

  const setProvince = useCallback((value: string) => {
    setProvinceInput(value)

    if (value === props.address.get('province')) return

    props.onChange?.(address => address.set('province', value))
  }, [props.address.get('province'), props.onChange])

  const updateProvinceFromInput = useCallback((value: string, matchProvinceCodes = false, allowFreeText = !hasProvinceOptions) => {
    if (normalizeAddressAutofillInput(value) === '') {
      setProvince('')
      return true
    }

    const provinceObj = findProvinceFromInput(value, matchProvinceCodes)
    if (provinceObj) {
      setProvince(provinceObj.get('name'))
      return true
    }

    if (allowFreeText) {
      setProvince(value)
      return true
    }

    return false
  }, [findProvinceFromInput, hasProvinceOptions, setProvince])

  const onChangeProvince = useCallback((e: SyntheticEvent, value: { name: string, label: string } | string | null) => {
    updateProvinceFromInput(typeof value === 'string' ? value : (value?.name ?? ''), true, !hasProvinceOptions)
  }, [hasProvinceOptions, updateProvinceFromInput])

  const onChangeProvinceInput = useCallback((e: SyntheticEvent, value: string) => {
    setProvinceInput(value)

    if (normalizeAddressAutofillInput(value) === '' || !hasProvinceOptions || findProvinceFromInput(value)) {
      updateProvinceFromInput(value, false, !hasProvinceOptions)
    }
  }, [findProvinceFromInput, hasProvinceOptions, updateProvinceFromInput])

  const onBlurProvinceText = useCallback((e: FocusEvent<HTMLInputElement>) => {
    if (!updateProvinceFromInput(e.target.value, true, !hasProvinceOptions)) {
      setProvinceInput(props.address.get('province'))
    }
  }, [hasProvinceOptions, props.address, updateProvinceFromInput])

  useEffect(() => {
    setProvinceInput(props.address.get('province'))
  }, [props.address.get('province')])

  useEffect(() => {
    if (props.address.get('province') && hasProvinceOptions) {
      updateProvinceFromInput(props.address.get('province'), true, false)
    }
  }, [hasProvinceOptions, props.address.get('province'), updateProvinceFromInput])

  const checkNotEmpty = useCallback((value: string) => value !== '', [])

  const nameValid = useMemo(() => checkNotEmpty(props.address.get('name')), [props.address.get('name')])
  const addressValid = useMemo(() => checkNotEmpty(props.address.get('address')), [props.address.get('address')])
  const cityValid = useMemo(() => checkNotEmpty(props.address.get('city')), [props.address.get('city')])

  const countryValid = useMemo(() => props.address.get('country') > 0, [props.address.get('country')])

  const phoneValid = useMemo(() => {
    const phone = props.address.get('phone')
    const isInternational = !['CA', 'US'].includes(selectedCountry?.get('alpha2_code') ?? '')
    const countryCode = getSupportedPhoneCountryCode(selectedCountry?.get('alpha2_code'))

    return isInternational
      ? checkNotEmpty(phone.trim()) && phoneNumberIsPossible(phone, countryCode)
      : phone.trim() === '' || phoneNumberIsPossible(phone, countryCode)
  }, [selectedCountry, props.address.get('phone')])

  const provinceValid = useMemo(() => {
    // If we don't have a list of provinces, just check that it's not empty
    if (!listProvinces || listProvinces.count() === 0) { return checkNotEmpty(props.address.get('province')) }

    // Confirm it's a valid province otherwise
    return !!findProvinceFromInput(props.address.get('province'))
  }, [props.address.get('province'), listProvinces, findProvinceFromInput])

  const postalCodeValid = useMemo(() => {
    let valid = true
    if (selectedCountry) {
      if (selectedCountry.get('name') === 'Canada') {
        valid = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(props.address.get('postal_code'))
      } else if (selectedCountry.get('name') === 'United States') {
        valid = /(^\d{5}$)|(^\d{5}-\d{4}$)/.test(props.address.get('postal_code'))
      }
    }
    return valid
  }, [props.address.get('postal_code'), selectedCountry])

  const postalCodeLabel = useMemo(() => {
    return isUSAddress ? t('ZIP Code') : t('Postal code')
  }, [isUSAddress, t])

  const postalCodeError = useMemo(() => {
    return isUSAddress ? t('Please enter a valid ZIP Code') : t('Please enter a valid postal code')
  }, [isUSAddress, t])

  const addressFormValid = useMemo(() => {
    return nameValid && addressValid && cityValid && provinceValid && countryValid && phoneValid && postalCodeValid
  }, [nameValid, addressValid, cityValid, provinceValid, countryValid, phoneValid, postalCodeValid])

  useEffect(() => {
    props.onValidCheck(addressFormValid)
  }, [addressFormValid])

  const renderCountryInput = useCallback((params: AutocompleteRenderInputParams) => {
    return <TextField
      {...params}
      label={t('Country')}
      name="country"
      error={!countryValid}
      inputProps={{
        ...params.inputProps,
        autoComplete: 'country-name',
        onBlur: (e) => {
          params.inputProps.onBlur?.(e)
          onBlurCountryInput(e)
        },
      }}
    />
  }, [t, countryValid, onBlurCountryInput])

  const renderProvinceInput = useCallback((params: AutocompleteRenderInputParams) => {
    return <TextField
      {...params}
      label={t('Province / State')}
      name="province"
      error={!provinceValid}
      inputProps={{
        ...params.inputProps,
        autoComplete: 'address-level1',
        onBlur: (e) => {
          params.inputProps.onBlur?.(e)
          onBlurProvinceText(e)
        },
      }}
    />
  }, [t, provinceValid, onBlurProvinceText])

  return <Grid
    container
    spacing={2}
  >
    <Grid item xs={12} sm={6}>
      <TextField
        label={t('Full name')}
        value={props.address.get('name')}
        error={!nameValid}
        onChange={onChangeName}
        autoFocus
        autoComplete="name"
        disabled={props.disabled}
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        label={t('Address Line 1')}
        value={props.address.get('address')}
        error={!addressValid}
        onChange={onChangeAddress}
        autoComplete="address-line1"
        disabled={props.disabled}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label={t('Address Line 2')}
        value={props.address.get('address2')}
        onChange={onChangeAddress2}
        autoComplete="address-line2"
        disabled={props.disabled}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <PhoneField
        label={t('Phone number')}
        defaultCountry={props.address.get('country')}
        value={props.address.get('phone')}
        onChange={onChangePhone}
        error={!phoneValid}
        helperText={props.address.get('phone') !== '' && !phoneValid ? t('Please enter a valid phone number') : ''}
        disabled={props.disabled}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label={t('City')}
        value={props.address.get('city')}
        error={!cityValid}
        onChange={onChangeCity}
        autoComplete="address-level2"
        disabled={props.disabled}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label={postalCodeLabel}
        value={props.address.get('postal_code')}
        error={!postalCodeValid}
        helperText={props.address.get('postal_code') !== '' && !postalCodeValid ? postalCodeError : ''}
        onChange={onChangePostalCode}
        autoComplete="postal-code"
        disabled={props.disabled}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <Autocomplete
        options={countryOptions}
        open={countryOpen}
        value={countryOptionValue}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={onChangeCountry}
        onInputChange={onChangeCountryInput}
        onOpen={onOpenCountry}
        onClose={onCloseCountry}
        autoComplete
        autoHighlight
        disabled={props.disabled}
        renderInput={renderCountryInput}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <Autocomplete
        freeSolo={!hasProvinceOptions}
        forcePopupIcon={hasProvinceOptions}
        options={provinceOptions}
        value={provinceOptionValue}
        inputValue={provinceInput}
        getOptionLabel={option => typeof option === 'string' ? option : option.label}
        isOptionEqualToValue={(option, value) => typeof value !== 'string' && option.name === value.name}
        onChange={onChangeProvince}
        onInputChange={onChangeProvinceInput}
        autoComplete
        autoHighlight
        disabled={props.disabled}
        renderInput={renderProvinceInput}
      />
    </Grid>
    <Grid item xs={12}>
      { props.onChangeSave ? <>
        <FormControlLabel
          control={<Checkbox
            checked={props.save}
            onChange={onChangeSave}
          />}
          label={t('Save address in my account')}
          disabled={props.disabled}
        /><br />
      </> : null }
      <Collapse in={props.save}>
        <FormControlLabel
          control={<Checkbox
            checked={props.address.get('default_shipping')}
            onChange={onChangeDefaultShipping}
          />}
          label={t('Set as default shipping address')}
          disabled={props.disabled}
        />
        <br/>
        <FormControlLabel
          control={<Checkbox
            checked={props.address.get('default_billing')}
            onChange={onChangeDefaultBilling}
          />}
          label={t('Set as default billing address')}
          disabled={props.disabled}
        />
      </Collapse>
    </Grid>
  </Grid>
}
